import { useState } from 'react';
import { X, Loader2, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInviteMember } from '../../api/hooks/useTeams';
import { Alert } from '../feedback/Alert';

interface InviteMemberModalProps {
  teamId: number;
  onClose: () => void;
}

export function InviteMemberModal({ teamId, onClose }: InviteMemberModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [error, setError] = useState<string | null>(null);

  const inviteMember = useInviteMember();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    try {
      await inviteMember.mutateAsync({
        teamId,
        data: { email: email.trim(), role },
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send invite');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-background-card rounded-xl border border-border shadow-xl mx-4"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">Invite Member</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background-elevated transition-colors"
          >
            <X size={20} className="text-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">
              Email Address
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">
              Role
            </label>
            <div className="flex gap-3">
              <label className="flex-1">
                <input
                  type="radio"
                  name="role"
                  value="member"
                  checked={role === 'member'}
                  onChange={() => setRole('member')}
                  className="sr-only"
                />
                <div
                  className={`
                    h-full p-3 rounded-lg border cursor-pointer transition-colors
                    ${role === 'member'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                    }
                  `}
                >
                  <div className="font-medium text-text-primary">Member</div>
                  <div className="text-xs text-text-secondary">Can view and use the team</div>
                </div>
              </label>
              <label className="flex-1">
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={role === 'admin'}
                  onChange={() => setRole('admin')}
                  className="sr-only"
                />
                <div
                  className={`
                    h-full p-3 rounded-lg border cursor-pointer transition-colors
                    ${role === 'admin'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                    }
                  `}
                >
                  <div className="font-medium text-text-primary">Admin</div>
                  <div className="text-xs text-text-secondary">Can invite and remove members</div>
                </div>
              </label>
            </div>
          </div>

          {error && (
            <Alert variant="error">{error}</Alert>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={inviteMember.isPending}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
            >
              {inviteMember.isPending ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Invite'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
